<script lang="ts" setup>
import PaginationTable from '@/components/tables/pagination-table/logic/PaginationTable.vue';
import type {
  PaginationQueryAction,
  PaginationQueryState,
} from '@/components/tables/pagination-table/types';
import { useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useSpecificInvoiceTableColumns } from './specific-invoice-table-columns';
import SpecificInvoiceTableRows from './specific-invoice-table-rows.vue';
import type { SpecificInvoiceTableInvoice } from './types';

defineProps<{
  rows: SpecificInvoiceTableInvoice[];
  totalRows: number;
  filters?: PaginationQueryState;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'row-click', invoice: SpecificInvoiceTableInvoice): void;
  (e: 'navigate-to-project', invoice: SpecificInvoiceTableInvoice): void;
  (e: 'query-change', action: PaginationQueryAction): void;
}>();

const tableRef = ref<HTMLElement | null>(null);
const { width } = useElementSize(tableRef, { width: 9999, height: 0 });

// Breakpoints based on container width.
const HIDE_INVOICE_DATE_BELOW = 1500;
const HIDE_VAT_BELOW = 1380;
const HIDE_APPROVER_BELOW = 1260;
const HIDE_INVOICE_NUMBER_BELOW = 1160;

const allColumns = [
  ...useSpecificInvoiceTableColumns(),
  { key: '_actions', sortable: false, columnWidth: '48px' },
];

const columns = computed(() =>
  allColumns.filter((col) => {
    if (col.key === 'invoiceDate' && width.value < HIDE_INVOICE_DATE_BELOW)
      return false;
    if (col.key === 'vat' && width.value < HIDE_VAT_BELOW) return false;
    if (col.key === 'approver' && width.value < HIDE_APPROVER_BELOW)
      return false;
    if (col.key === 'invoiceNumber' && width.value < HIDE_INVOICE_NUMBER_BELOW)
      return false;
    return true;
  }),
);

function onQueryChange(action: PaginationQueryAction) {
  emit('query-change', action);
}
</script>

<template>
  <div class="invoice-table" ref="tableRef">
    <PaginationTable
      :columns="columns"
      :rows="rows"
      :total-items="totalRows"
      :row-class="(row) => row.status"
      :query="filters"
      :is-loading="isLoading"
      @query-change="onQueryChange"
      @row-click="emit('row-click', $event)"
    >
      <template #cell="{ row, column, value }">
        <SpecificInvoiceTableRows
          :row="row"
          :column="column as string"
          :value="value as unknown"
          @navigate-to-project="emit('navigate-to-project', row)"
        />
      </template>
    </PaginationTable>
  </div>
</template>

<style scoped lang="scss">
.invoice-table {
  :deep(table tbody) {
    tr {
      cursor: pointer;
    }

    tr:nth-child(even) td {
      background-color: #f2f3f6;
    }

    tr:hover td {
      background-color: #e2e4ea;
    }

    tr td:last-child {
      vertical-align: middle;
    }
  }
}
</style>
